import { useState, useEffect, useRef } from 'react';
import { useGetRequest } from '../hooks/useGetRequest';
import moment from 'moment';
import { io } from 'socket.io-client';
import { useSearchParams } from 'react-router-dom';
import { getSocket, disconnectSocket } from '../utils/socket';
import { postRequest } from '../utils/axiosRequests';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import md5 from 'md5';
import { QRCodeSVG } from 'qrcode.react';

// ── Data ──────────────────────────────────────────────────────────────────────
// const PATIENTS = [
//   {
//     id: 1,
//     initials: 'RK',
//     name: 'Rafiq Karim',
//     serial: 'A-001',
//     status: 'consulting',
//     color: '#1e9bd7',
//   },
//   {
//     id: 2,
//     initials: 'SA',
//     name: 'Sabina Akter',
//     serial: 'A-002',
//     status: 'waiting',
//     color: '#8e44ad',
//   },
//   {
//     id: 3,
//     initials: 'MR',
//     name: 'Mizan Rahman',
//     serial: 'A-003',
//     status: 'waiting',
//     color: '#e74c3c',
//   },
//   {
//     id: 4,
//     initials: 'FJ',
//     name: 'Farida Jahan',
//     serial: 'A-004',
//     status: 'waiting',
//     color: '#27ae60',
//   },
//   {
//     id: 5,
//     initials: 'TH',
//     name: 'Tariq Hossain',
//     serial: 'A-005',
//     status: 'waiting',
//     color: '#f39c12',
//   },
//   {
//     id: 6,
//     initials: 'NB',
//     name: 'Nasrin Begum',
//     serial: 'A-006',
//     status: 'waiting',
//     color: '#2c3e50',
//   },
//   {
//     id: 7,
//     initials: 'JA',
//     name: 'Jahangir Alam',
//     serial: 'A-007',
//     status: 'completed',
//     color: '#16a085',
//   },
//   {
//     id: 8,
//     initials: 'PC',
//     name: 'Parvin Chowdhury',
//     serial: 'A-008',
//     status: 'waiting',
//     color: '#c0392b',
//   },
//   {
//     id: 9,
//     initials: 'KM',
//     name: 'Kamal Mia',
//     serial: 'A-009',
//     status: 'completed',
//     color: '#d35400',
//   },
//   {
//     id: 10,
//     initials: 'RB',
//     name: 'Roksana Begum',
//     serial: 'A-010',
//     status: 'waiting',
//     color: '#2980b9',
//   },
//   {
//     id: 11,
//     initials: 'SI',
//     name: 'Sharif Islam',
//     serial: 'A-011',
//     status: 'waiting',
//     color: '#7f8c8d',
//   },
//   {
//     id: 12,
//     initials: 'LK',
//     name: 'Laila Khatun',
//     serial: 'A-012',
//     status: 'waiting',
//     color: '#1abc9c',
//   },
// ];

// ── Status badge config ───────────────────────────────────────────────────────
const STATUS_CONFIG = {
  consulting: { label: 'Consulting', bg: '#d1ecff', color: '#0a5a9c' },
  waiting: { label: 'Waiting', bg: '#fff3cd', color: '#856404' },
  completed: { label: 'Completed', bg: '#d4edda', color: '#155724' },
};

// ── QR Code generator (canvas-based, no external lib) ────────────────────────
function QRCodeCanvas({ value, size = 140 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Simple QR placeholder — renders a styled placeholder since qrcodejs isn't available in React env
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const s = size;
    ctx.clearRect(0, 0, s, s);

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, s, s);

    // Draw a realistic-looking QR pattern using a seeded grid
    const cell = s / 21;
    const seed = [...value].reduce((a, c) => a + c.charCodeAt(0), 0);
    const rand = (i) => ((seed * 9301 + i * 49297) % 233280) / 233280;

    ctx.fillStyle = '#0a3d62';

    // Finder patterns (3 corners)
    const drawFinder = (x, y) => {
      // Outer 7x7
      ctx.fillRect(x * cell, y * cell, 7 * cell, 7 * cell);
      ctx.fillStyle = '#fff';
      ctx.fillRect((x + 1) * cell, (y + 1) * cell, 5 * cell, 5 * cell);
      ctx.fillStyle = '#0a3d62';
      ctx.fillRect((x + 2) * cell, (y + 2) * cell, 3 * cell, 3 * cell);
    };
    drawFinder(0, 0);
    drawFinder(14, 0);
    drawFinder(0, 14);

    // Data modules
    for (let row = 0; row < 21; row++) {
      for (let col = 0; col < 21; col++) {
        // Skip finder areas
        if (
          (row < 8 && col < 8) ||
          (row < 8 && col > 12) ||
          (row > 12 && col < 8)
        )
          continue;
        if (rand(row * 21 + col) > 0.5) {
          ctx.fillStyle = '#0a3d62';
          ctx.fillRect(col * cell + 0.5, row * cell + 0.5, cell - 1, cell - 1);
        }
      }
    }
  }, [value, size]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ display: 'block' }}
    />
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
function PanelHeader({ icon, title, badge }) {
  return (
    <div style={styles.panelHeader}>
      <span style={{ fontSize: '1rem', color: '#fff' }}>{icon}</span>
      <h2 style={styles.panelTitle}>{title}</h2>
      {badge && <span style={styles.badgeCount}>{badge}</span>}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.waiting;
  return (
    <span
      style={{ ...styles.statusBadge, background: cfg.bg, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
}

const getStatus = (status) => {
  if (status === 'new') return 'Waiting';
  else if (status === 'consulting') return 'Consulting';
  else return 'Unknown';
};

function AppointmentTable({ patients }) {
  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            {['#', 'Patient Name', 'Serial No', 'Status'].map((h) => (
              <th key={h} style={styles.th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {patients.length === 0 ? (
            <tr>
              <td
                colSpan={4}
                style={{ ...styles.td, textAlign: 'center', color: '#aaa', fontStyle: 'italic', padding: '18px 16px' }}
              >
                No appointments
              </td>
            </tr>
          ) : (
            patients.map((p, index) => {
              const isActive = p.status === 'consulting';
              const rowBg = isActive ? '#e0f4ff' : index % 2 === 0 ? '#ffffff' : '#f4f8fb';
              return (
                <tr
                  key={p.id}
                  className="appt-row"
                  style={{ ...(isActive ? styles.activeRow : styles.row), background: rowBg }}
                >
                  <td style={styles.td}>{index + 1}</td>
                  <td style={styles.td}>
                    <div style={styles.patientCell}>
                      <div style={{ ...styles.avatar, background: '#1e9bd7' }}>
                        {'PN'}
                      </div>
                      <span style={styles.patientName}>{p.patientId.name}</span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={isActive ? styles.serialActive : styles.serial}>
                      {p.serialNumber}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function QRSection({ doctorId, organizationId, userGeneratedToken }) {
  const QR_URL = `https://prescripto.org/show-appointments?doctorId=${doctorId}&organizationId=${organizationId}&userToken=${userGeneratedToken}`;

  return (
    <div style={styles.qrSection}>
      {/* <PanelHeader icon="⬛" title="Scan to see appoinment on your mobile" /> */}
      <div style={styles.qrBody}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            // padding: '16px',
            background: '#fff',
          }}
        >
          <QRCodeSVG value={QR_URL} size={64} level="L" includeMargin={true} />
        </div>
        <div style={styles.qrInfo}>
          {/* <h3 style={styles.qrInfoTitle}>See Online Serial</h3> */}
          <p style={styles.qrInfoText}>
            QR স্ক্যান করুন — অ্যাপয়েন্টমেন্ট আপডেট আপনার মোবাইলে দেখুন
          </p>
          {/* <div style={styles.qrUrl}>
            <span>🔗</span>
            <a
              href={QR_URL}
              target="_blank"
              rel="noreferrer"
              style={{ wordBreak: 'break-all', fontSize: '12px' }}
            >
              {QR_URL}
            </a>
          </div> */}
        </div>
      </div>
    </div>
  );
}

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch)
    return `https://www.youtube.com/embed/${shortMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${shortMatch[1]}`;
  const longMatch = url.match(/[?&]v=([^?&]+)/);
  if (longMatch)
    return `https://www.youtube.com/embed/${longMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${longMatch[1]}`;
  if (url.includes('youtube.com/embed/')) return url;
  return null;
};

function VideoPanel({ videoUrl }) {
  const embedUrl = getYouTubeEmbedUrl(videoUrl);
  if (!embedUrl) return null;
  return (
    <div style={styles.panel}>
      <PanelHeader icon="▶" title="Health Awareness Channel" />
      <div style={styles.videoBody}>
        <div style={styles.videoWrap}>
          <iframe
            src={embedUrl}
            title="Health Awareness Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{ width: '100%', height: '100%', display: 'block' }}
          />
        </div>
        <div style={styles.videoLabel}>
          <LiveDot />
          <span>
            Now Playing: &nbsp;<strong>Health Awareness</strong>
          </span>
        </div>
        <div style={styles.noticeBoard}>
          <p style={styles.noticeTitle}>
            <span style={{ color: '#1e9bd7', marginRight: 4 }}>📢</span> Notice
          </p>
          <p style={styles.noticeText}>
            Please keep your token number handy. The doctor will call you by
            your token. Maintain silence and keep mobile phones on silent mode
            inside the clinic.
          </p>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_DOCTOR_IMG = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><circle cx='50' cy='50' r='50' fill='%23e8f4fb'/><circle cx='50' cy='36' r='18' fill='%23228093'/><ellipse cx='50' cy='85' rx='28' ry='20' fill='%23228093'/></svg>`;

function DoctorInfoPanel({ doctorInfo }) {
  const { name, speciality, degree, medicalName, image, experience } =
    doctorInfo;

  const infoRow = (icon, label, value) =>
    value ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          background: '#f8fbfd',
          borderRadius: 10,
          padding: '12px 16px',
          border: '1px solid #d0e8f5',
        }}
      >
        <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{icon}</span>
        <div>
          <p
            style={{
              fontSize: '.68rem',
              color: '#6b8799',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '.5px',
              marginBottom: 2,
            }}
          >
            {label}
          </p>
          <p style={{ fontWeight: 600, color: '#0d1f2d', fontSize: '.9rem' }}>
            {value}
          </p>
        </div>
      </div>
    ) : null;

  return (
    <div
      style={{
        ...styles.panel,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <PanelHeader icon="🩺" title="Consulting Doctor" />

      {/* Profile hero — gradient background */}
      <div
        style={{
          background:
            'linear-gradient(160deg, #1a6b82 0%, #228093 55%, #1e9bd7 100%)',
          padding: '28px 24px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
        }}
      >
        {/* Photo */}
        <div
          style={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            overflow: 'hidden',
            flexShrink: 0,
            border: '4px solid rgba(255,255,255,0.85)',
            boxShadow: '0 8px 28px rgba(0,0,0,.22)',
            background: 'rgba(255,255,255,0.15)',
          }}
        >
          {image ? (
            <img
              src={`data:image/png;base64, ${image}`}
              alt={name || 'Doctor'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <img
              src={DEFAULT_DOCTOR_IMG}
              alt="Doctor"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          )}
        </div>

        {/* Name */}
        <div style={{ textAlign: 'center' }}>
          <h3
            style={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 800,
              fontSize: '1.35rem',
              color: '#fff',
              marginBottom: 4,
              lineHeight: 1.2,
            }}
          >
            {name || '—'}
          </h3>

          {/* Degree */}
          {degree && (
            <p
              style={{
                color: 'rgba(255,255,255,.8)',
                fontSize: '.8rem',
                marginBottom: 10,
                lineHeight: 1.5,
              }}
            >
              {degree.join(', ')}
            </p>
          )}

          {/* Speciality badge */}
          {speciality && (
            <span
              style={{
                background: 'rgba(255,255,255,0.18)',
                color: '#fff',
                fontSize: '.75rem',
                fontWeight: 600,
                padding: '5px 16px',
                borderRadius: 20,
                border: '1px solid rgba(255,255,255,0.45)',
                display: 'inline-block',
              }}
            >
              {speciality}
            </span>
          )}
        </div>
      </div>

      {/* Info rows */}
      <div
        style={{
          padding: '18px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          flex: 1,
        }}
      >
        {/* {infoRow('🏥', 'Clinic / Hospital', medicalName)} */}
        {/* {infoRow('🕐', 'Chamber Hours', visitingHour)} */}
        {infoRow('🏆', 'Experience', experience)}

        {/* Patient notice */}
        <div
          style={{
            background: 'linear-gradient(135deg, #fff8e1 0%, #fffde7 100%)',
            border: '1px solid #ffe082',
            borderRadius: 10,
            padding: '14px 16px',
            marginTop: 'auto',
          }}
        >
          <p
            style={{
              fontWeight: 700,
              color: '#856404',
              marginBottom: 6,
              fontSize: '.84rem',
            }}
          >
            📢 Patient Notice
          </p>
          <p style={{ fontSize: '.8rem', color: '#6b5900', lineHeight: 1.65 }}>
            Please keep your token number handy. The doctor will call you by
            your token. Maintain silence and keep mobile phones on silent mode
            inside the clinic.
          </p>
        </div>
      </div>
    </div>
  );
}

function DoctorHeaderCard({ doctorInfo }) {
  const { name, speciality, degree, medicalName, image } = doctorInfo;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      {/* Simple round avatar */}
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          overflow: 'hidden',
          flexShrink: 0,
          background: 'rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.1rem',
          fontWeight: 700,
          color: '#fff',
        }}
      >
        {image ? (
          <img
            src={`data:image/png;base64, ${image}`}
            alt={name || 'Doctor'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <img
            src={DEFAULT_DOCTOR_IMG}
            alt="Doctor"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        )}
      </div>

      {/* Name / Speciality / Org */}
      <div>
        <div
          style={{
            fontFamily: "'Syne', sans-serif",
            fontWeight: 700,
            color: '#fff',
            fontSize: '1rem',
            lineHeight: 1.25,
          }}
        >
          {name || '—'}
        </div>
        {speciality && (
          <div
            style={{
              color: '#4cd137',
              fontSize: '.82rem',
              fontWeight: 600,
              lineHeight: 1.35,
            }}
          >
            {speciality}
          </div>
        )}
        <div style={{ color: '#a0cfe8', fontSize: '.78rem', lineHeight: 1.35 }}>
          {medicalName}
        </div>
      </div>
    </div>
  );
}

function AnnounceBanner({ message, onClose }) {
  if (!message) return null;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        height: 46,
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(90deg, #b03a2e 0%, #e74c3c 40%, #e74c3c 60%, #b03a2e 100%)',
        boxShadow: '0 -3px 18px rgba(0,0,0,.35)',
        overflow: 'hidden',
      }}
    >
      {/* Label pill */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          background: 'rgba(0,0,0,0.28)',
          height: '100%',
          padding: '0 20px',
          borderRight: '1px solid rgba(255,255,255,0.18)',
        }}
      >
        <span style={{ fontSize: '1.1rem' }}>📢</span>
        <span
          style={{
            fontFamily: "'Syne', sans-serif",
            fontWeight: 800,
            fontSize: '.82rem',
            color: '#fff',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          Notice
        </span>
      </div>

      {/* Scrolling text */}
      <div style={{ flex: 1, overflow: 'hidden', height: '100%', position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            whiteSpace: 'nowrap',
            fontFamily: "'Syne', sans-serif",
            fontWeight: 700,
            fontSize: '1rem',
            color: '#fff',
            animation: 'tickerScroll 22s linear infinite',
          }}
        >
          {message}
        </div>
      </div>

      {/* Dismiss */}
      <button
        onClick={onClose}
        title="Dismiss"
        style={{
          flexShrink: 0,
          background: 'rgba(0,0,0,0.22)',
          border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: '50%',
          width: 30,
          height: 30,
          marginRight: 12,
          cursor: 'pointer',
          color: '#fff',
          fontSize: '.9rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ✕
      </button>
    </div>
  );
}

function LiveDot() {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: '#e74c3c',
        animation: 'pulse 1.2s infinite',
        flexShrink: 0,
      }}
    />
  );
}

// const organizationId = '62b15e73bcb2efc0c8dee0ce';
// const doctorId = '634d008db0c2db42c614e836';
const currentDate = moment().format('YYYY-MM-DD');

const AppointmentAd = () => {
  const [waitingPatients, setWaitingPatients] = useState([]);
  const [waitingPatientsCount, setWaitingPatientsCount] = useState(0);
  const [now, setNow] = useState(new Date());
  const [appointmentsToday, setAppointmentsToday] = useState(0);
  const [medicalName, setMedicalName] = useState('Medical Name');
  const [videoUrl, setVideoUrl] = useState(null);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [name, setName] = useState('');
  const [speciality, setSpeciality] = useState('');
  const [degree, setDegree] = useState([]);
  const [bmdc, setBmdc] = useState('');
  const [experience, setExperience] = useState('');
  const [image, setImage] = useState('');
  const [announceMessage, setAnnounceMessage] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(null);

  // if (userInfo?.doctorDetails?.isVideoCall) {

  const [searchParams] = useSearchParams();
  const doctorId = searchParams.get('doctorId');
  const organizationId = searchParams.get('organizationId');
  const token = searchParams.get('token');
  const userToken = searchParams.get('userToken');
  const navigate = useNavigate();
  const userGeneratedToken = md5(`${doctorId}${moment().format('YYYY-MM-DD')}`);

  // }

  // const socket = getSocket();

  // useEffect(() => {
  //   const socket = getSocket();
  //   if (!socket) return;

  //   socket.on('privateMessage', (message) => {
  //     console.log('message received:', message);
  //   });

  //   return () => socket.off('privateMessage'); // cleanup listener
  // }, []);

  // useEffect(() => {
  //   // client-side
  //   if (socket) {
  //     if (socket && socket.connected) {
  //       console.log('socket conneted1 ', socket.id);
  //     }
  //     socket.on('privateMessage', (data) => {
  //       console.log('message from cleint ', data);
  //       const message = data;
  //       console.log('come from data ', message);
  //       setAppointmentsToday(data);
  //     });
  //     socket.on('disconnect', () => {
  //       console.log('disconnected ', socket.id); // undefined
  //     });
  //   }
  // }, [doctorId]);

  // if (socket && socket.connected) {
  //   socket.on('disconnect', () => {
  //     console.log('disconnected ', socket.id); // undefined
  //   });
  // }

  const { isLoading: isAppointmentLoading, refetch: getWaitingPatients } =
    useGetRequest(
      'get-waiting-patients',
      `appointments/appointment-ad?organizationId=${organizationId}&doctorId=${doctorId}&startDate=${currentDate}&endDate=${currentDate}`,
      (data) => {
        setWaitingPatients(data.appointments);
        setWaitingPatientsCount(data.total);
        setVideoUrl(data.youtubeUrl || null);
        const activeTitle =
          data.announce?.status === 'on' ? data.announce?.title || null : null;
        setAnnounceMessage(activeTitle);
        // If a new/different message arrives, re-show even if previously dismissed
        if (activeTitle && activeTitle !== bannerDismissed) {
          setBannerDismissed(null);
        }
      },
      (e) => {
        console.log(e.message);
      },
    );

  const { isLoading: isOrgInfoLoading, refetch: getOrganizationDetails } =
    useGetRequest(
      'get-organization-details',
      `organizations/${organizationId}`,
      (data) => {
        setMedicalName(data.name);
      },
      (e) => {
        console.log(e.message);
      },
    );

  const { isLoading: isDoctorInfoLoading, refetch: getDoctorDetails } =
    useGetRequest(
      'get-Doctor-details',
      `auth/doctor-info/${doctorId}`,
      (data) => {
        console.log('doctor-details: ', data);
        setName(data.name || '');
        setSpeciality(data.speciality[0]?.name || '');
        setDegree(data?.degree || []);
        setExperience(data?.experience || '');
        setImage(data?.imageContent || '');
      },
      (e) => {
        console.log(e.message);
      },
    );

  const checkToken = async () => {
    if (token) {
      await postRequest('auth/ad-token-check', { doctorId, token })
        .then((data) => {
          console.log('token ok', data);
          if (!data.success) {
            navigate('/404', {});
          }
        })
        .catch((error) => {
          console.log(error);
          navigate('/404', {});
        });
    } else if (userToken) {
      await postRequest('auth/ad-user-token-check', { doctorId, userToken })
        .then((data) => {
          console.log('token ok', data);
          if (!data.success) {
            navigate('/404', {});
          }
        })
        .catch((error) => {
          console.log(error);
          navigate('/404', {});
        });
    } else {
      navigate('/404', {});
    }
  };

  useEffect(() => {
    checkToken();
  }, []);

  useEffect(() => {
    getOrganizationDetails();
    getWaitingPatients();
    getDoctorDetails();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      getWaitingPatients();
    }, 5000);

    return () => clearInterval(interval); // ✅ cleanup on unmount
  }, [getWaitingPatients]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const dateStr = now.toLocaleDateString('en-BD', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('en-BD', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #f0f7fc; }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: .5; transform: scale(1.3); }
        }
        @keyframes tickerScroll {
          from { transform: translateX(100vw) translateY(-50%); }
          to   { transform: translateX(-100%) translateY(-50%); }
        }
        .appt-row:hover { background: #e8f4fb !important; }
      `}</style>

      {announceMessage && announceMessage !== bannerDismissed && (
        <AnnounceBanner
          message={announceMessage}
          onClose={() => setBannerDismissed(announceMessage)}
        />
      )}
      <div style={styles.root}>
        {/* HEADER */}
        <header style={styles.header}>
          <div>
            <div style={styles.brand}>{'Prescripto Live'}</div>
            <div style={{ ...styles.dateChip, marginTop: 4 }}>
              📅 {dateStr} &nbsp;|&nbsp; 🕐 {timeStr}
            </div>
          </div>
          <DoctorHeaderCard
            doctorInfo={{ name, speciality, degree, medicalName, image }}
          />
        </header>

        {/* MAIN GRID */}
        <div style={{ ...styles.mainGrid, gridTemplateColumns: '1fr 1fr' }}>
          {/* LEFT COLUMN */}
          <div style={styles.leftCol}>
            {[
              { key: 'new', label: 'New Patients', icon: '🆕' },
              { key: 'followup', label: 'Follow Up', icon: '🔄' },
              { key: 'report', label: 'Report', icon: '📋' },
            ].map(({ key, label, icon }) => {
              const group = waitingPatients.filter(
                (p) => (p.visitType || 'new') === key,
              );
              return (
                <div key={key} style={styles.panel}>
                  <PanelHeader
                    icon={icon}
                    title={label}
                    badge={`${group.length} Patient${group.length !== 1 ? 's' : ''}`}
                  />
                  <AppointmentTable patients={group} />
                </div>
              );
            })}
          </div>

          {/* RIGHT: Video or Doctor Info */}
          <div>
            {videoUrl ? (
              <VideoPanel videoUrl={videoUrl} />
            ) : (
              <DoctorInfoPanel
                doctorInfo={{
                  name,
                  speciality,
                  degree,
                  image,
                  experience,
                  medicalName,
                }}
              />
            )}
            {
              <QRSection
                doctorId={doctorId}
                organizationId={organizationId}
                userGeneratedToken={userGeneratedToken}
              />
            }
          </div>
        </div>
      </div>
    </>
  );
};

export default AppointmentAd;

// ── Styles ────────────────────────────────────────────────────────────────────
const C = {
  primary: '#228093',
  accent: '#1e9bd7',
  soft: '#e8f4fb',
  surface: '#ffffff',
  muted: '#6b8799',
  border: '#d0e8f5',
  text: '#0d1f2d',
};

const styles = {
  root: {
    fontFamily: "'DM Sans', sans-serif",
    background: '#f0f7fc',
    minHeight: '100vh',
    color: C.text,
  },
  header: {
    background: C.primary,
    padding: '14px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 2px 12px rgba(0,0,0,.2)',
  },
  brand: {
    fontFamily: "'Syne', sans-serif",
    fontWeight: 800,
    fontSize: '1.45rem',
    color: '#fff',
    letterSpacing: '.5px',
  },
  dateChip: {
    fontSize: '.85rem',
    color: '#b8d9ee',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
    padding: '28px 32px',
  },
  leftCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  panel: {
    background: C.surface,
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(10,61,98,.08)',
    border: `1px solid ${C.border}`,
  },
  panelHeader: {
    background: C.primary,
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  panelTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: '1rem',
    fontWeight: 700,
    color: '#fff',
    margin: 0,
    letterSpacing: '.3px',
  },
  badgeCount: {
    marginLeft: 'auto',
    background: C.accent,
    color: '#fff',
    fontSize: '.75rem',
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 20,
  },
  tableWrap: {
    maxHeight: 210,
    overflowY: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    background: C.soft,
    padding: '7px 12px',
    fontSize: '.78rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '.6px',
    color: C.muted,
    borderBottom: `2px solid ${C.border}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    textAlign: 'left',
  },
  row: {
    borderBottom: '1px solid #eaf3fb',
  },
  activeRow: {
    background: '#e0f4ff',
    borderLeft: `4px solid ${C.accent}`,
    borderBottom: '1px solid #eaf3fb',
  },
  td: {
    padding: '5px 12px',
    fontSize: '.86rem',
  },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '.7rem',
    color: '#fff',
    flexShrink: 0,
  },
  patientCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  patientName: {
    fontWeight: 500,
    lineHeight: 1.2,
    fontSize: '.92rem',
  },
  serial: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3px 10px',
    borderRadius: 20,
    background: C.soft,
    border: `1.5px solid ${C.border}`,
    color: C.primary,
    fontWeight: 700,
    fontSize: '.78rem',
    letterSpacing: '.4px',
  },
  serialActive: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3px 10px',
    borderRadius: 20,
    background: C.accent,
    border: `1.5px solid ${C.accent}`,
    color: '#fff',
    fontWeight: 700,
    fontSize: '.78rem',
    letterSpacing: '.4px',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: '.72rem',
    fontWeight: 600,
  },
  // QR
  qrSection: {
    display: 'flex',
    background: C.surface,
    borderRadius: 16,
    border: `1px solid ${C.border}`,
    boxShadow: '0 4px 20px rgba(10,61,98,.07)',
    overflow: 'hidden',
  },
  qrBody: {
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 24,
  },
  qrBox: {
    background: '#fff',
    padding: 10,
    borderRadius: 12,
    border: `2px solid ${C.accent}`,
    boxShadow: '0 2px 12px rgba(30,155,215,.15)',
    flexShrink: 0,
  },
  qrInfo: {},
  qrInfoTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: '1rem',
    fontWeight: 700,
    color: C.primary,
    marginBottom: 6,
  },
  qrInfoText: {
    fontSize: '.83rem',
    color: C.muted,
    lineHeight: 1.6,
  },
  qrUrl: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
    padding: '5px 12px',
    background: C.soft,
    borderRadius: 8,
    fontSize: '.78rem',
    color: C.accent,
    fontWeight: 500,
    border: `1px solid ${C.border}`,
    wordBreak: 'break-all',
  },
  // Video
  videoBody: {
    padding: 16,
  },
  videoWrap: {
    position: 'relative',
    borderRadius: 10,
    overflow: 'hidden',
    background: '#000',
    aspectRatio: '16/9',
    boxShadow: '0 4px 16px rgba(0,0,0,.15)',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  videoLabel: {
    marginTop: 12,
    fontSize: '.82rem',
    color: C.muted,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  noticeBoard: {
    marginTop: 12,
    padding: 12,
    borderRadius: 12,
    background: C.soft,
    border: `1px solid ${C.border}`,
  },
  noticeTitle: {
    fontSize: '.82rem',
    color: C.muted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '.5px',
    marginBottom: 4,
  },
  noticeText: {
    fontSize: '.85rem',
    color: C.text,
    lineHeight: 1.6,
  },
};
